import { flushSync } from "react-dom";
import { v4 } from "uuid";
import { iChatData } from "../../types";
import { getChatId } from "../../utils/chat/chatId";
import { getChatTitle } from "../../utils/chat/chatTitle";
import { getSessionId } from "../../utils/sessionId";
import { dropEmptyContentMessages } from "../../streaming/finalizeGuards";
import { Fragment, isRealReply, joinFragments } from "../../services/voice/liveTranscript";
import type { ShownAnswer, HeardAnswer, Turn, VoiceSession } from "./types";
import { VoiceRuntime, VOICE_EXCHANGE, voiceLog, isRequestRunning } from "./runtime";
import { latestPrompt, withCleanPrompts, bubbleText } from "./chatEntries";
import type { TranscriptIO } from "./transcriptIO";
import type { Bubbles } from "./bubbles";

export const createHeardAnswers = (rt: VoiceRuntime, transcriptIO: TranscriptIO, bubbles: Bubbles) => {
  const { live, deferredRef } = rt;
  const { cleanerOf } = transcriptIO;
  const { applyTargets, nothingAhead } = bubbles;

  const fileAnswer = (session: VoiceSession, turn: Turn, fragments: Fragment[], text: string) => {
    const { state: current, history: historyCtx, handleOpenSocketConnection: openTurn } = live.current;
    const chat: iChatData[] = current.chatData;
    const userId = current.userInfo?.id ? String(current.userInfo.id) : "";
    const held = turn.held ? chat.find((entry) => entry.id === turn.held?.replyId) : undefined;
    const heldPrompt = held && Array.isArray(held.prompt) && held.prompt.length >= 2 ? held.prompt : null;
    if (held && heldPrompt) {
      const prior = heldPrompt.slice(0, -2);
      const prompt = [...heldPrompt.slice(0, -1), { role: "assistant", content: text }];
      current.setChatData((prev: iChatData[]) =>
        prev.map((entry) => (entry.id === held.id ? { ...entry, message: text, voiceReply: text, prompt, shouldNotShowFirstMessage: false } : entry)),
      );
      session.targets = session.targets.filter((target) => target.entryId !== held.id);
      session.targets.push({ fragments, entryId: held.id, sent: text, shown: text, filed: true });
      historyCtx.setCallSaveHistoryAPIFlag({ flag: true, method: "PUT" });
      voiceLog("heard.answer", { traceId: held.traceId, replaced: true, chars: text.length });
      openTurn({
        requestType: VOICE_EXCHANGE,
        userInput: heldPrompt[heldPrompt.length - 2]?.content ?? "",
        voiceReply: text,
        messages: prior,
        chatId: getChatId(),
        chatTitle: getChatTitle(chat),
        sessionId: getSessionId(),
        userId,
        traceId: held.traceId,
      });
      return;
    }
    const prior = dropEmptyContentMessages(latestPrompt(chat));
    const traceId = v4().slice(0, 8);
    const id = v4();
    current.setChatData((prev: iChatData[]) => [
      ...prev,
      {
        id,
        message: text,
        isUserMessage: false,
        prompt: [...prior, { role: "assistant", content: text }],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        responseType: "ai_assistant",
        voiceReply: text,
        traceId,
        ...(session.id ? { liveSessionId: session.id } : {}),
      },
    ]);
    session.targets.push({ fragments, entryId: id, sent: text, shown: text, filed: true });
    historyCtx.setCallSaveHistoryAPIFlag({ flag: true, method: "PUT" });
    voiceLog("heard.answer", { traceId, replaced: false, chars: text.length });
    openTurn({
      requestType: VOICE_EXCHANGE,
      userInput: cleanerOf(session).best([...turn.late, ...turn.heard]),
      voiceReply: text,
      messages: prior,
      chatId: getChatId(),
      chatTitle: getChatTitle(chat),
      sessionId: getSessionId(),
      userId,
      traceId,
    });
  };

  const fileAnswers = (session: VoiceSession) => {
    if (session.filings.length > 0 || deferredRef.current.length > 0 || isRequestRunning()) return;
    const cleaner = cleanerOf(session);
    while (session.answers.length > 0) {
      const { turn, fragments, shown } = session.answers[0];
      const cleaned = cleaner.cleaned(fragments);
      if (!cleaned.complete) return;
      session.answers.shift();
      if (shown) fileShownAnswer(session, turn, shown, cleaned.text);
      else if (cleaned.text) fileAnswer(session, turn, fragments, cleaned.text);
    }
  };

  const settleAnswer = (session: VoiceSession) => {
    const answer = session.answer;
    session.answer = null;
    if (!answer || answer.fragments.length === 0) return;
    cleanerOf(session).finish("assistant", answer.fragments);
    const show = !answer.shown && nothingAhead(session);
    session.answers.push(answer);
    if (show) showAnswer(session, answer);
    else if (answer.shown) settleShownAnswer(session, answer);
    fileAnswers(session);
  };

  const showLiveAnswer = (session: VoiceSession) => {
    const answer = session.answer;
    if (!answer) return;
    if (answer.shown) {
      applyTargets(session);
      return;
    }
    if (!isRealReply(joinFragments(answer.fragments)) || !nothingAhead(session)) return;
    showAnswer(session, answer);
  };

  const settleShownAnswer = (session: VoiceSession, answer: HeardAnswer) => {
    const target = session.targets.find((candidate) => candidate.entryId === answer.shown?.entryId);
    const text = cleanerOf(session).best(answer.fragments);
    if (!target || !target.sent || target.sent === text) return;
    const partial = target.sent;
    target.sent = text;
    live.current.state.setChatData((prev: iChatData[]) => withCleanPrompts(prev, partial, text));
    applyTargets(session);
  };

  const showAnswer = (session: VoiceSession, item: HeardAnswer) => {
    const { state: current } = live.current;
    const cleaner = cleanerOf(session);
    const { turn, fragments } = item;
    const sent = cleaner.best(fragments);
    const message = bubbleText(cleaner, fragments);
    const chat: iChatData[] = current.chatData;
    const held = turn.held ? chat.find((entry) => entry.id === turn.held?.replyId) : undefined;
    const heldPrompt = held && Array.isArray(held.prompt) && held.prompt.length >= 2 ? held.prompt : null;
    if (held && heldPrompt) {
      const prompt = [...heldPrompt.slice(0, -1), { role: "assistant", content: sent }];
      flushSync(() =>
        current.setChatData((prev: iChatData[]) =>
          prev.map((entry) => (entry.id === held.id ? { ...entry, message, prompt, shouldNotShowFirstMessage: false } : entry)),
        ),
      );
      item.shown = { entryId: held.id, traceId: held.traceId ?? "", held: { message: held.message, voiceReply: held.voiceReply, prompt: heldPrompt } };
    } else {
      const prior = dropEmptyContentMessages(latestPrompt(chat));
      const traceId = v4().slice(0, 8);
      const id = v4();
      flushSync(() =>
        current.setChatData((prev: iChatData[]) => [
          ...prev,
          {
            id,
            message,
            isUserMessage: false,
            prompt: [...prior, { role: "assistant", content: sent }],
            isLoading: false,
            destinationList: [],
            singleSelectedDestination: false,
            responseType: "ai_assistant",
            traceId,
            ...(session.id ? { liveSessionId: session.id } : {}),
          },
        ]),
      );
      item.shown = { entryId: id, traceId, held: null };
    }
    const { entryId, traceId } = item.shown;
    session.targets = session.targets.filter((target) => target.entryId !== entryId);
    session.targets.push({ fragments, entryId, sent, shown: message, filed: false });
    voiceLog("heard.shown", { traceId, replaced: !!item.shown.held, chars: sent.length });
  };

  const fileShownAnswer = (session: VoiceSession, turn: Turn, shown: ShownAnswer, text: string) => {
    const { state: current, history: historyCtx, handleOpenSocketConnection: openTurn } = live.current;
    const entry = current.chatData.find((candidate: iChatData) => candidate.id === shown.entryId);
    if (!entry || !text) {
      session.targets = session.targets.filter((target) => target.entryId !== shown.entryId);
      if (!entry) return;
      const original = shown.held;
      current.setChatData((prev: iChatData[]) =>
        original
          ? prev.map((candidate) =>
              candidate.id === shown.entryId
                ? { ...candidate, message: original.message, voiceReply: original.voiceReply, prompt: original.prompt, shouldNotShowFirstMessage: true }
                : candidate,
            )
          : prev.filter((candidate) => candidate.id !== shown.entryId),
      );
      historyCtx.setCallSaveHistoryAPIFlag({ flag: true, method: "PUT" });
      return;
    }
    const prompt: any[] = Array.isArray(entry.prompt) ? entry.prompt : [];
    const prior = prompt.slice(0, shown.held ? -2 : -1);
    const userInput = shown.held ? prompt[prompt.length - 2]?.content ?? "" : cleanerOf(session).best([...turn.late, ...turn.heard]);
    current.setChatData((prev: iChatData[]) =>
      prev.map((candidate) => (candidate.id === shown.entryId ? { ...candidate, voiceReply: text } : candidate)),
    );
    session.targets.forEach((target) => {
      if (target.entryId === shown.entryId) target.filed = true;
    });
    applyTargets(session);
    historyCtx.setCallSaveHistoryAPIFlag({ flag: true, method: "PUT" });
    voiceLog("heard.answer", { traceId: shown.traceId, replaced: !!shown.held, chars: text.length });
    openTurn({
      requestType: VOICE_EXCHANGE,
      userInput,
      voiceReply: text,
      messages: prior,
      chatId: getChatId(),
      chatTitle: getChatTitle(current.chatData),
      sessionId: getSessionId(),
      userId: current.userInfo?.id ? String(current.userInfo.id) : "",
      traceId: shown.traceId,
    });
  };

  return { fileAnswer, fileAnswers, settleAnswer, showLiveAnswer, settleShownAnswer, showAnswer, fileShownAnswer };
};

export type HeardAnswers = ReturnType<typeof createHeardAnswers>;
